import {ChangeEvent, forwardRef, useMemo} from "react";
import {DeviceFilters} from "../../types/filters";
import FilterSection from "./FilterSection";

type Props = {
    allBrands: string[];
    availableBrands: string[];
    activeFilters: DeviceFilters;
    onFilterChange: (
        filter: keyof DeviceFilters,
        value: string | number[] | boolean,
        e: ChangeEvent<HTMLInputElement>
    ) => void;
    onClearFilter: () => void;
};

const DISPLAY_OPTIONS = new Map<string, number[]>([
    ["<5", [0, 5]],
    ["5.1-5.5", [5.1, 5.5]],
    ["5.6-6", [5.6, 6]],
    ["6.1-6.5", [6.1, 6.5]],
    ["6.6-6.8", [6.6, 6.8]],
    [">6.8", [6.8, 100]],
]);

const RAM_OPTIONS = new Map<string, number[]>([
    ["<3 GB", [0, 3]],
    ["3 GB", [2.9, 3.1]],
    ["4 GB", [3.9, 4.1]],
    ["6 GB", [5.9, 6.1]],
    ["8 GB", [7.9, 8.1]],
    ["12 GB", [11.9, 12.1]],
    [">12 GB", [12.1, 100]],
]);

const MAIN_CAMERA_OPTIONS = new Map<string, number[]>([
    ["<8 MP", [0, 8]],
    ["8-19 MP", [8, 19]],
    ["20-47 MP", [20, 47]],
    ["48-63 MP", [48, 63]],
    ["64-99 MP", [64, 99]],
    ["100 MP", [99, 101]],
    ["108 MP", [107, 109]],
    ["200 MP", [199, 201]],
]);

const FRONT_CAMERA_OPTIONS = new Map<string, number[]>([
    ["<6 MP", [0, 5.9]],
    ["6-11 MP", [6, 11]],
    ["12-15 MP", [12, 15]],
    ["16-20 MP", [16, 20]],
    ["21-30 MP", [21, 30]],
    [">30 MP", [30.1, 100]],
]);

const REFRESH_RATE_OPTIONS = new Map<string, number[]>([
    ["60 Hz", [0, 60]],
    ["90 Hz", [89, 91]],
    ["120 Hz", [119, 121]],
    ["144 Hz", [143, 145]],
    ["165+ Hz", [164, 1000]],
]);

const STORAGE_OPTIONS = new Map<string, number[]>([
    ["<64 GB", [0, 63]],
    ["64 GB", [63, 65]],
    ["128 GB", [127, 129]],
    ["256 GB", [255, 257]],
    ["512 GB", [511, 513]],
    ["1 TB", [1023, 1025]],
]);

const RELEASE_YEAR_OPTIONS = new Map<string, number[]>([
    ["2026", [2026, 2026]],
    ["2025", [2025, 2025]],
    ["2024", [2024, 2024]],
    ["2023 and older", [0, 2023]],
]);

function buildStringOptions(
    options: string[], // Now accepts a simple array of strings
    active: string[],
    onCheck: (value: string, e: ChangeEvent<HTMLInputElement>) => void
) {
    return options.map((label) => ({
        key: label,
        label: label, // No count suffix since we're passing a simple array
        checked: active.includes(label),
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            onCheck(label, e);
        },
    }));
}

function buildRangeOptions(
    options: Map<string, number[]>,
    active: number[][],
    onCheck: (filter: number[], e: ChangeEvent<HTMLInputElement>) => void
) {
    return Array.from(options.entries()).map(([label, range]) => ({
        key: label,
        label,
        checked: active.some((arr) => range[0] === arr[0] && range[1] === arr[1]),
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            onCheck(range, e);
        },
    }));
}

const PRIORITY_BRANDS = ["Apple", "Samsung", "Google", "Xiaomi", "Honor", "Motorola", "Nothing", "Oppo"];

const FilterBlock = forwardRef<HTMLDivElement, Props>(
    ({ allBrands, availableBrands, activeFilters, onFilterChange, onClearFilter }, ref) => {

        const brandOptions = useMemo(() => {
            // These are the brands that currently have phones in your filtered list
            const brandsWithPhones = new Set(availableBrands);
            const active = (activeFilters.brands as string[]) || [];
            // Combine Priority list + Brands that actually have phones
            const allUnique = Array.from(new Set([...PRIORITY_BRANDS, ...allBrands]));

            return allUnique
                .sort((a, b) => {
                    const aPri = PRIORITY_BRANDS.includes(a);
                    const bPri = PRIORITY_BRANDS.includes(b);
                    if (aPri && bPri) return a.localeCompare(b);
                    if (aPri) return -1;
                    if (bPri) return 1;
                    return a.localeCompare(b);
                })
                .map((brandName) => {
                    const hasPhones = brandsWithPhones.has(brandName);
                    const isChecked = active.includes(brandName);
                    return {
                        key: brandName,
                        label: brandName,
                        checked: isChecked,
                        disabled: !hasPhones && !isChecked,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                            e.stopPropagation();

                            if (!hasPhones && !isChecked) return;
                            onFilterChange("brands", brandName, e);
                        },
                    };
                });
        }, [availableBrands, allBrands, activeFilters.brands, onFilterChange]);


        const sections = useMemo(() => ([
            {
                title: "Brand",
                // We pass the priority-based list.
                // Your FilterSection component will handle the "Show More"
                // toggle if you set a limit.
                options: brandOptions,
                columns: 2 as const,
                // If you want the 'Show More' to reveal the full list,
                // you might need to pass allBrandOptions and handle it inside FilterSection.
                limit: PRIORITY_BRANDS.length,
            },
            // --- 2. THE OLD FILTERS (The classics) ---
            {
                title: "Display Size",
                options: buildRangeOptions(DISPLAY_OPTIONS, (activeFilters.screen_size as number[][]) || [], (v, e) => onFilterChange("screen_size", v, e)),
                columns: 2 as const,
            },
            {
                title: "RAM",
                options: buildRangeOptions(RAM_OPTIONS, (activeFilters.ram as number[][]) || [], (v, e) => onFilterChange("ram", v, e)),
                columns: 2 as const,
            },
            {
                title: "Main Camera",
                options: buildRangeOptions(MAIN_CAMERA_OPTIONS, (activeFilters.main_camera as number[][]) || [], (v, e) => onFilterChange("main_camera", v, e)),
                columns: 2 as const,
            },
            {
                title: "Front Camera",
                options: buildRangeOptions(FRONT_CAMERA_OPTIONS, (activeFilters.front_camera as number[][]) || [], (v, e) => onFilterChange("front_camera", v, e)),
                columns: 2 as const,
            },
            {
                title: "Storage",
                options: buildRangeOptions(STORAGE_OPTIONS, (activeFilters.storage as number[][]) || [], (v, e) => onFilterChange("storage", v, e)),
                columns: 2 as const,
            },
            {
                title: "Screen Refresh Rate",
                options: buildRangeOptions(REFRESH_RATE_OPTIONS, (activeFilters.refresh_rate as number[][]) || [], (v, e) => onFilterChange("refresh_rate", v, e)),
                columns: 2 as const,
            },
            {
                title: "Release Date",
                options: buildRangeOptions(RELEASE_YEAR_OPTIONS, (activeFilters.release_year as number[][]) || [], (v, e) => onFilterChange("release_year", v, e)),
                columns: 2 as const,
            },

            // --- Expanded/Advanced Filters (Only visible if expanded) ---
            {
                title: "Memory Slot",
                options: buildStringOptions(["Dedicated Slot", "No Slot"], (activeFilters.memory_slot as string[]) || [], (v, e) => onFilterChange("memory_slot", v, e)),
                columns: 1 as const,
            },
            {
                title: "SIM Support",
                options: buildStringOptions(["Dual SIM", "Single SIM", "eSIM Support"], (activeFilters.sim_type as string[]) || [], (v, e) => onFilterChange("sim_type", v, e)),
                columns: 1 as const,
            },
            {
                title: "Biometrics",
                options: buildStringOptions(["Fingerprint", "Face ID"], (activeFilters.biometrics as string[]) || [], (v, e) => onFilterChange("biometrics", v, e)),
                columns: 2 as const,
            },
            {
                title: "Connectivity & Features",
                options: [
                    { key: "5g", label: "5G Support", checked: !!activeFilters.has_5g, onChange: (e: ChangeEvent<HTMLInputElement>) => { e.stopPropagation(); onFilterChange("has_5g", e.target.checked, e) } },
                    { key: "nfc", label: "NFC", checked: !!activeFilters.has_nfc, onChange: (e: ChangeEvent<HTMLInputElement>) => { e.stopPropagation(); onFilterChange("has_nfc", e.target.checked, e) } },
                    { key: "fast_charging", label: "Fast Charging (>18W)", checked: !!activeFilters.fast_charging, onChange: (e: ChangeEvent<HTMLInputElement>) => { e.stopPropagation(); onFilterChange("fast_charging", e.target.checked, e) } },
                    { key: "wireless_charging", label: "Wireless Charging", checked: !!activeFilters.wireless_charging, onChange: (e: ChangeEvent<HTMLInputElement>) => { e.stopPropagation(); onFilterChange("wireless_charging", e.target.checked, e) } },
                ],
                columns: 1 as const,
            }
        ]), [activeFilters, brandOptions, onFilterChange]);

        return (
            <div className="bg-gray-300 flex justify-center rounded-xl" ref={ref}>
                <div className="flex flex-col gap-8 p-8">

                    <button
                        onClick={onClearFilter}
                        className="text-sm text-red-600 hover:underline"
                    >
                        Clear Filters
                    </button>

                    {sections.map((section) => (
                        <FilterSection
                            key={section.title}
                            title={section.title}
                            options={section.options}
                            columns={section.columns}
                            limit={section.limit}
                        />
                    ))}

                </div>
            </div>
        );
    }
);

export default FilterBlock;